import { useState, type ReactNode } from "react";
import {
  ArrowRight,
  ChartSpline,
  ChevronDown,
  Settings2,
  Sparkles,
  TrendingDown,
  TrendingUp,
  WalletCards,
} from "lucide-react";

import type {
  DashboardRateWatchComparisonProduct,
  DashboardRateWatchFeeLine,
  DashboardRateWatchHistoryPoint,
  DashboardSnapshot,
} from "../../lib/dashboard";
import { trimTrailingZeros } from "../../lib/dashboard";
import { RateExplorerView } from "../calculators/rate-explorer/rate-explorer-view";
import {
  pricingModeForLoanProduct,
  pricingModeNoteForMode,
} from "../calculators/rate-explorer/rate-explorer-view.helpers";
import { Button } from "../ui/button";
import { PageHeader } from "../ui/page-header";
import { Slider } from "../ui/slider";
import { cn } from "../../lib/utils";

// Default loan amount used when the consumer hasn't entered a target price yet.
const FALLBACK_LOAN_AMOUNT = 500_000;

export function RateWatchView({
  inAppNotificationsEnabled = false,
  onOpenAdvisor,
  onOpenFinancials,
  onOpenSettings,
  snapshot,
}: {
  inAppNotificationsEnabled?: boolean;
  onOpenAdvisor: () => void;
  onOpenFinancials: () => void;
  onOpenSettings: () => void;
  snapshot: DashboardSnapshot;
}) {
  const hasRateWatchData =
    snapshot.rateWatch.status === "ready" ||
    snapshot.rateWatch.status === "preview";

  if (!hasRateWatchData) {
    return (
      <section className="space-y-5">
        <section className="surface-panel overflow-hidden bg-card p-6 sm:p-8">
          <div className="space-y-4">
            <PageHeader
              title="We'll show your live rate watch once Sill has a pricing run for your current profile."
              description="This page uses pricing data from your current Sill profile once a real profile exists."
              descriptionClassName="max-w-2xl leading-7"
            />
            {snapshot.rateWatch.missingInputs.length > 0 ? (
              <p className="text-sm leading-6 text-muted-foreground">
                Still needed: {snapshot.rateWatch.missingInputs.join(", ")}.
              </p>
            ) : null}
            <div className="flex flex-wrap gap-3">
              <Button onClick={onOpenFinancials}>
                <WalletCards className="h-4 w-4" />
                Open calculators
              </Button>
              <Button variant="outline" onClick={onOpenAdvisor}>
                <ChartSpline className="h-4 w-4" />
                Ask Sill what is missing
              </Button>
              {inAppNotificationsEnabled ? (
                <Button variant="outline" onClick={onOpenSettings}>
                  <Settings2 className="h-4 w-4" />
                  Tune notification settings
                </Button>
              ) : null}
            </div>
          </div>
        </section>
      </section>
    );
  }

  return (
    <RateWatchReady
      inAppNotificationsEnabled={inAppNotificationsEnabled}
      onOpenAdvisor={onOpenAdvisor}
      onOpenFinancials={onOpenFinancials}
      onOpenSettings={onOpenSettings}
      snapshot={snapshot}
    />
  );
}

function RateWatchReady({
  inAppNotificationsEnabled,
  onOpenAdvisor,
  onOpenFinancials,
  onOpenSettings,
  snapshot,
}: {
  inAppNotificationsEnabled: boolean;
  onOpenAdvisor: () => void;
  onOpenFinancials: () => void;
  onOpenSettings: () => void;
  snapshot: DashboardSnapshot;
}) {
  const rateWatch = snapshot.rateWatch;
  const comparisonProducts =
    rateWatch.comparisonProducts && rateWatch.comparisonProducts.length > 0
      ? rateWatch.comparisonProducts
      : [
          {
            badge: { slug: "ready", label: "Ready", tone: "default" },
            primaryOverlay: true,
            baseRatePercent: rateWatch.baseRatePercent,
            estimatedPaymentMonthly: rateWatch.scenarioPayment,
            feeLines: rateWatch.feeLines ?? [],
            history: rateWatch.history,
            id: "conventional_30",
            label: "30-yr fixed",
            locationCode: rateWatch.locationCode ?? null,
            llpaPoints: rateWatch.llpaPoints,
            loanTermMonths: 360,
            personalizedRatePercent: rateWatch.personalizedRatePercent,
            pricingLines: rateWatch.pricingLines ?? [],
            pricingSourceAsOf: null,
            pricingSourceName: null,
            primary: true,
            product: "conventional",
            shortLabel: "30 yr",
            status: rateWatch.status === "preview" ? "preview" : "ready",
            targetPrice: rateWatch.targetPrice ?? null,
          } satisfies DashboardRateWatchComparisonProduct,
        ];
  const primaryProduct =
    comparisonProducts.find((product) => product.primary) ??
    comparisonProducts[0] ??
    null;
  const [activeProductId, setActiveProduct] = useState<string | null>(null);
  const activeProduct =
    (activeProductId
      ? comparisonProducts.find((product) => product.id === activeProductId)
      : null) ??
    primaryProduct ??
    null;
  const hasBackendTarget =
    typeof rateWatch.targetPrice === "number" && rateWatch.targetPrice > 0;
  const targetBasisPrice =
    hasBackendTarget && typeof rateWatch.targetPrice === "number"
      ? rateWatch.targetPrice
      : snapshot.targetPrice > 0
        ? snapshot.targetPrice
        : FALLBACK_LOAN_AMOUNT;
  const targetBasisLocation =
    hasBackendTarget && snapshot.targetPrice !== rateWatch.targetPrice
      ? rateWatch.locationCode || snapshot.targetArea || null
      : snapshot.targetArea || rateWatch.locationCode || null;
  const loanAmount = targetBasisPrice;
  const [ratesShift, setRatesShift] = useState(0);
  const [points, setPoints] = useState(0);

  const primaryHistory = primaryProduct?.history ?? rateWatch.history;
  const primaryLatest = primaryHistory.at(-1) ?? null;
  const primaryPrevious =
    primaryHistory.length > 1
      ? primaryHistory[primaryHistory.length - 2]
      : null;
  const activeHistory = activeProduct?.history ?? rateWatch.history;
  const chartHistory = buildProductHistory({
    history: activeHistory,
    productId: activeProduct?.id ?? "conventional_30",
    ratesShift,
  });

  const storedLatest = activeHistory.at(-1) ?? null;
  const storedPrevious =
    activeHistory.length > 1 ? activeHistory[activeHistory.length - 2] : null;

  const activeBaseDelta =
    typeof storedLatest?.baseRatePercent === "number" &&
    typeof storedPrevious?.baseRatePercent === "number"
      ? storedLatest.baseRatePercent - storedPrevious.baseRatePercent
      : activeProduct?.primary
        ? rateWatch.weeklyDeltaPercent
        : null;
  const primaryBaseDelta =
    typeof primaryLatest?.baseRatePercent === "number" &&
    typeof primaryPrevious?.baseRatePercent === "number"
      ? primaryLatest.baseRatePercent - primaryPrevious.baseRatePercent
      : rateWatch.weeklyDeltaPercent;
  const primaryPaymentDelta =
    typeof primaryLatest?.estimatedPaymentMonthly === "number" &&
    typeof primaryPrevious?.estimatedPaymentMonthly === "number"
      ? primaryLatest.estimatedPaymentMonthly -
        primaryPrevious.estimatedPaymentMonthly
      : typeof primaryLatest?.baseRatePercent === "number" &&
          typeof primaryPrevious?.baseRatePercent === "number" &&
          primaryProduct
        ? monthlyPayment(
            loanAmount,
            primaryLatest.baseRatePercent * 100,
            primaryProduct.loanTermMonths,
          ) -
          monthlyPayment(
            loanAmount,
            primaryPrevious.baseRatePercent * 100,
            primaryProduct.loanTermMonths,
          )
        : null;
  const activePaymentDelta =
    typeof storedLatest?.estimatedPaymentMonthly === "number" &&
    typeof storedPrevious?.estimatedPaymentMonthly === "number"
      ? storedLatest.estimatedPaymentMonthly -
        storedPrevious.estimatedPaymentMonthly
      : typeof storedLatest?.baseRatePercent === "number" &&
          typeof storedPrevious?.baseRatePercent === "number" &&
          activeProduct
        ? monthlyPayment(
            loanAmount,
            storedLatest.baseRatePercent * 100,
            activeProduct.loanTermMonths,
          ) -
          monthlyPayment(
            loanAmount,
            storedPrevious.baseRatePercent * 100,
            activeProduct.loanTermMonths,
          )
        : null;

  if (!activeProduct) {
    return null;
  }
  const activeRateExplanation =
    activeProduct.rateExplanation ??
    (activeProduct.primary ? (rateWatch.rateExplanation ?? null) : null);
  const activeLenderExecutions =
    activeProduct.lenderExecutions ??
    (activeProduct.primary ? (rateWatch.lenderExecutions ?? []) : []);
  const activePricingMode =
    activeProduct.pricingMode ??
    (activeProduct.primary ? rateWatch.pricingMode : null) ??
    pricingModeForLoanProduct(activeProduct.product);
  const activePricingModeNote =
    activeProduct.pricingModeNote ??
    (activeProduct.primary ? rateWatch.pricingModeNote : null) ??
    pricingModeNoteForMode(activePricingMode);

  return (
    <section className="space-y-5">
      <PageHeader
        title="Rate watch"
        description="Your primary rate watch plus every currently supported comparison product. Switch lanes or model a rate move."
        descriptionClassName="max-w-xl"
        actions={
          inAppNotificationsEnabled ? (
            <Button size="sm" variant="outline" onClick={onOpenSettings}>
              <Settings2 className="h-4 w-4" />
              Notification settings
            </Button>
          ) : null
        }
      >
        {rateWatch.baseRateAsOfLabel ? (
          <p className="text-xs font-semibold text-muted-foreground">
            Pricing as of {rateWatch.baseRateAsOfLabel}
          </p>
        ) : null}
        <p className="text-xs font-semibold text-muted-foreground">
          Modeled target:{" "}
          <span className="text-foreground">
            {formatTargetBasis(targetBasisPrice, targetBasisLocation)}
          </span>
        </p>
      </PageHeader>

      <WhatChangedStrip
        onOpenAdvisor={onOpenAdvisor}
        baseRateDelta={primaryBaseDelta}
        paymentDelta={primaryPaymentDelta}
        product={primaryProduct ?? activeProduct}
        loanAmount={loanAmount}
      />

      <HeroChartCard
        activeProduct={activeProduct}
        baseRateDelta={activeBaseDelta}
        chartHistory={chartHistory}
        loanAmount={loanAmount}
        paymentDelta={activePaymentDelta}
        ratesShift={ratesShift}
      />

      {activeRateExplanation ? (
        // Side-by-side on lg+: compact vertical product column on the
        // left, rate decomposition panel on the right. Stacks on smaller
        // screens (column first, then explorer) so neither view is
        // squished. The per-tile target-basis line is dropped in column
        // mode because the page header already states the modeled basis.
        <div className="grid gap-5 lg:grid-cols-[minmax(360px,460px)_1fr]">
          <ProductStrip
            activeProduct={activeProduct}
            comparisonProducts={comparisonProducts}
            loanAmount={loanAmount}
            ratesShift={ratesShift}
            setActiveProduct={setActiveProduct}
            variant="column"
          />
          <section
            aria-label="Rate explanation"
            className="surface-panel space-y-4 bg-card p-5 sm:p-6"
          >
            <RateExplorerView
              executions={activeLenderExecutions}
              explanation={activeRateExplanation}
              loanContextLabel={activeProduct.label}
              pricingMode={activePricingMode}
              pricingModeNote={activePricingModeNote}
            />
            <ActiveProductFeePanel
              product={activeProduct}
              loanAmount={loanAmount}
            />
          </section>
        </div>
      ) : (
        <ProductStrip
          activeProduct={activeProduct}
          comparisonProducts={comparisonProducts}
          loanAmount={loanAmount}
          ratesShift={ratesShift}
          setActiveProduct={setActiveProduct}
        />
      )}

      <ScenarioSimulator
        activeProduct={activeProduct}
        loanAmount={loanAmount}
        points={points}
        ratesShift={ratesShift}
        setPoints={setPoints}
        setRatesShift={setRatesShift}
      />

      <PricingFAQ
        activeProduct={activeProduct}
        onOpenFinancials={onOpenFinancials}
      />

      <p className="text-center text-[11px] leading-6 text-muted-foreground">
        Today&apos;s quote is recalculated live from your latest profile, active
        target, and the newest observed rate. Charts foreground the last 30 days
        of base-rate history. The primary lane still merges market history with
        saved checkpoints when the target matches the profile, and every
        comparison lane uses the same observed-rate window. Payments are
        modeled, not lender-quoted.
      </p>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// What changed — narrative lead
// ─────────────────────────────────────────────────────────────────────

function WhatChangedStrip({
  baseRateDelta,
  loanAmount,
  onOpenAdvisor,
  paymentDelta,
  product,
}: {
  baseRateDelta: number | null;
  loanAmount: number;
  onOpenAdvisor: () => void;
  paymentDelta: number | null;
  product: DashboardRateWatchComparisonProduct;
}) {
  const hasRateDelta = typeof baseRateDelta === "number";
  const rateImproved = hasRateDelta && baseRateDelta < 0;
  const direction = hasRateDelta
    ? rateImproved
      ? "dropped"
      : baseRateDelta > 0
        ? "ticked up"
        : "held steady"
    : "held steady";

  const paymentCopy =
    typeof paymentDelta === "number"
      ? paymentDelta < 0
        ? `That's $${Math.abs(Math.round(paymentDelta)).toLocaleString()}/mo less on a ${formatCompactLoan(loanAmount)} balance.`
        : paymentDelta > 0
          ? `That's $${Math.round(paymentDelta).toLocaleString()}/mo more on a ${formatCompactLoan(loanAmount)} balance.`
          : "Your estimated payment didn't move."
      : null;

  const marketCopy =
    typeof baseRateDelta === "number"
      ? `Base rate moved ${formatSignedPercent(baseRateDelta)}.`
      : "Base rate held steady.";
  const pricingCopy = buildWhatChangedPricingCopy(product, loanAmount);

  return (
    <section className="flex flex-wrap items-center gap-4 rounded-[1.5rem] border border-primary/20 bg-primary/5 p-4 sm:p-5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-primary/20 bg-card">
        {rateImproved ? (
          <TrendingDown className="h-4 w-4 text-primary" />
        ) : (
          <TrendingUp className="h-4 w-4 text-primary" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-6 text-foreground">
          Rates{" "}
          <b className="font-semibold text-primary">
            {direction}
            {hasRateDelta ? ` ${formatPercentMagnitude(baseRateDelta)}` : ""}
          </b>{" "}
          this week.
          {paymentCopy ? ` ${paymentCopy}` : ""}
        </p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {marketCopy} {pricingCopy}
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={onOpenAdvisor}>
        <ChartSpline className="h-4 w-4" />
        Ask Sill what changed
      </Button>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Hero chart card — title, legend, dual-line chart, KPI strip
// ─────────────────────────────────────────────────────────────────────

function HeroChartCard({
  activeProduct,
  baseRateDelta,
  chartHistory,
  loanAmount,
  paymentDelta,
  ratesShift,
}: {
  activeProduct: DashboardRateWatchComparisonProduct;
  baseRateDelta: number | null;
  chartHistory: ProductHistoryPoint[];
  loanAmount: number;
  paymentDelta: number | null;
  ratesShift: number;
}) {
  const hasHistory = chartHistory.length > 0;
  const productCost = buildProductCostDisplay(activeProduct, loanAmount);
  const effectiveBase =
    typeof activeProduct.baseRatePercent === "number"
      ? activeProduct.baseRatePercent + ratesShift / 10000
      : (chartHistory.at(-1)?.baseRatePercent ?? null);
  const effectivePayment =
    ratesShift === 0 &&
    typeof activeProduct.estimatedPaymentMonthly === "number"
      ? activeProduct.estimatedPaymentMonthly
      : typeof effectiveBase === "number"
        ? monthlyPayment(
            loanAmount,
            effectiveBase * 100,
            activeProduct.loanTermMonths,
          )
        : null;

  return (
    <section className="surface-panel bg-card p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow-label">
            Rate history · {activeProduct.label}
            {activeProduct.status === "preview" ? " · preview" : null}
          </p>
          <h2 className="mt-1 text-[1.35rem] font-semibold leading-snug tracking-[-0.02em] text-foreground">
            Base rate at{" "}
            <span className="font-bold tabular-nums text-primary">
              {formatRatePercent(effectiveBase)}
            </span>
          </h2>
          {ratesShift !== 0 ? (
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-primary">
              Simulated shift {formatSignedBps(ratesShift)}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-4 pt-1 text-xs text-muted-foreground">
          <LegendSwatch label="Base rate" tone="market" />
        </div>
      </div>

      <div className="mt-4">
        {hasHistory ? (
          <RateChart
            history={chartHistory}
            loanAmount={loanAmount}
            termMonths={activeProduct.loanTermMonths}
          />
        ) : (
          <div className="flex min-h-[240px] items-center justify-center rounded-2xl bg-muted/30 text-sm text-muted-foreground">
            We&apos;ll plot a trend once Sill has more than one saved point for
            this lane.
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-y-4 border-t border-border pt-4 sm:grid-cols-3">
        <KpiCell
          delta={formatSignedPercent(baseRateDelta)}
          deltaGood={typeof baseRateDelta === "number" && baseRateDelta < 0}
          eyebrow="Base rate"
          sub={formatPricingSourceLabel(activeProduct.pricingSourceName)}
          value={formatRatePercent(effectiveBase)}
        />
        <KpiCell
          eyebrow={productCost.eyebrow}
          sub={productCost.sub}
          value={productCost.value}
        />
        <KpiCell
          delta={formatSignedMoney(paymentDelta, "mo")}
          deltaGood={typeof paymentDelta === "number" && paymentDelta < 0}
          eyebrow="Est. payment"
          sub={`Modeled on ${activeProduct.shortLabel}`}
          value={formatPayment(effectivePayment)}
        />
      </div>
    </section>
  );
}

function KpiCell({
  delta,
  deltaGood,
  eyebrow,
  sub,
  value,
  valueTone = "default",
}: {
  delta?: string;
  deltaGood?: boolean;
  eyebrow: string;
  sub: string;
  value: string;
  valueTone?: "default" | "primary";
}) {
  return (
    <div className="px-4 first:pl-0 last:pr-0 sm:border-r sm:border-border sm:last:border-r-0">
      <p className="eyebrow-label">{eyebrow}</p>
      <p
        className={cn(
          "display-figure mt-1 text-[1.75rem] leading-tight",
          valueTone === "primary" ? "text-primary" : "text-foreground",
        )}
      >
        {value}
      </p>
      {delta ? (
        <p
          className={cn(
            "mt-1 inline-flex items-center gap-1 text-xs font-semibold",
            deltaGood ? "text-primary" : "text-destructive",
          )}
        >
          {deltaGood ? (
            <TrendingDown className="h-3 w-3" />
          ) : (
            <TrendingUp className="h-3 w-3" />
          )}
          <span className="font-bold tabular-nums">{delta}</span>
        </p>
      ) : null}
      <p className="mt-1 text-xs leading-5 text-muted-foreground">{sub}</p>
    </div>
  );
}

function LegendSwatch({ label, tone }: { label: string; tone: "market" }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden="true"
        className={cn(
          "inline-block",
          tone === "market" &&
            "h-0 w-4 border-y-2 border-dashed border-muted-foreground",
        )}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Rate chart — base-rate SVG with hover tooltip
// ─────────────────────────────────────────────────────────────────────

interface ProductHistoryPoint {
  key: string;
  dateLabel: string;
  baseRatePercent: number | null;
  personalizedRatePercent: number | null;
}

function RateChart({
  history,
  loanAmount,
  termMonths,
}: {
  history: ProductHistoryPoint[];
  loanAmount: number;
  termMonths: number;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const W = 920;
  const H = 320;
  const padL = 56;
  const padR = 116;
  const padT = 32;
  const padB = 42;
  const iw = W - padL - padR;
  const ih = H - padT - padB;

  const values = history.flatMap((point) => {
    const items: number[] = [];
    if (typeof point.baseRatePercent === "number")
      items.push(point.baseRatePercent * 100);
    return items;
  });
  if (values.length === 0) {
    return null;
  }
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const min = Math.floor(minValue * 4) / 4 - 0.25;
  const max = Math.ceil(maxValue * 4) / 4 + 0.25;
  const span = Math.max(max - min, 0.1);

  const n = history.length;
  const x = (i: number) =>
    n === 1 ? padL + iw / 2 : padL + (i / (n - 1)) * iw;
  const y = (value: number) => padT + ih - ((value - min) / span) * ih;

  const ticks: number[] = [];
  for (
    let value = Math.ceil(min * 4) / 4;
    value <= max + 0.001;
    value += 0.25
  ) {
    ticks.push(Number(value.toFixed(2)));
  }

  const marketPath = buildLinePath(history, (p) => p.baseRatePercent, x, y);

  const last = history[history.length - 1];
  const lastMarketY =
    typeof last?.baseRatePercent === "number"
      ? y(last.baseRatePercent * 100)
      : null;

  return (
    <div className="overflow-x-auto rounded-[1.35rem] bg-muted/20 p-3 sm:p-4">
      <svg
        aria-label="Rate watch history chart"
        className="h-auto min-w-[720px] w-full"
        onMouseLeave={() => setHoverIndex(null)}
        viewBox={`0 0 ${W} ${H}`}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              stroke="var(--border-soft)"
              strokeDasharray="3 4"
              x1={padL}
              x2={W - padR}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              className="fill-muted-foreground"
              fontSize="10"
              fontWeight={500}
              textAnchor="end"
              x={padL - 10}
              y={y(tick) + 4}
            >
              {tick.toFixed(2)}%
            </text>
          </g>
        ))}

        {history.map((point, i) => (
          <text
            key={point.key}
            className="fill-muted-foreground"
            fontSize="10"
            fontWeight={500}
            textAnchor="middle"
            x={x(i)}
            y={H - 14}
          >
            {point.dateLabel}
          </text>
        ))}

        <line
          stroke="var(--border-soft)"
          strokeDasharray="2 4"
          x1={x(n - 1)}
          x2={x(n - 1)}
          y1={padT}
          y2={H - padB}
        />
        <text
          className="fill-muted-foreground"
          fontSize="9"
          fontWeight={700}
          letterSpacing="0.22em"
          textAnchor="middle"
          x={x(n - 1)}
          y={padT - 8}
        >
          TODAY
        </text>

        {marketPath ? (
          <path
            d={marketPath}
            fill="none"
            stroke="var(--muted-foreground)"
            strokeDasharray="5 4"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        ) : null}

        {history.map((point, i) => {
          const elements: ReactNode[] = [];
          if (typeof point.baseRatePercent === "number") {
            elements.push(
              <circle
                key={`m-${point.key}`}
                cx={x(i)}
                cy={y(point.baseRatePercent * 100)}
                fill="var(--card)"
                r="3.5"
                stroke="var(--muted-foreground)"
                strokeWidth="1.5"
              />,
            );
          }
          return <g key={point.key}>{elements}</g>;
        })}

        {history.map((_, i) => (
          <rect
            key={`hit-${i}`}
            fill="transparent"
            height={ih}
            onMouseEnter={() => setHoverIndex(i)}
            style={{ cursor: "crosshair" }}
            width={n > 1 ? iw / (n - 1) : iw}
            x={n > 1 ? x(i) - iw / (n - 1) / 2 : padL}
            y={padT}
          />
        ))}

        {lastMarketY !== null ? (
          <g>
            <line
              opacity="0.4"
              stroke="var(--muted-foreground)"
              strokeDasharray="2 3"
              x1={x(n - 1)}
              x2={W - padR + 10}
              y1={lastMarketY}
              y2={lastMarketY}
            />
            <text
              className="fill-muted-foreground"
              fontSize="9"
              fontWeight={700}
              letterSpacing="0.18em"
              x={W - padR + 14}
              y={lastMarketY - 6}
            >
              BASE RATE
            </text>
            <text
              className="font-bold tabular-nums fill-foreground"
              fontSize="15"
              fontWeight={700}
              x={W - padR + 14}
              y={lastMarketY + 12}
            >
              {formatRatePercent(last?.baseRatePercent ?? null)}
            </text>
          </g>
        ) : null}

        {hoverIndex !== null
          ? renderHoverTooltip({
              boxHeight: 64,
              boxWidth: 182,
              history,
              index: hoverIndex,
              innerHeight: ih,
              loanAmount,
              padB,
              padR,
              padT,
              termMonths,
              totalWidth: W,
              x,
              y,
            })
          : null}
      </svg>
    </div>
  );
}

function renderHoverTooltip({
  boxHeight,
  boxWidth,
  history,
  index,
  innerHeight,
  loanAmount,
  padB,
  padR,
  padT,
  termMonths,
  totalWidth,
  x,
  y,
}: {
  boxHeight: number;
  boxWidth: number;
  history: ProductHistoryPoint[];
  index: number;
  innerHeight: number;
  loanAmount: number;
  padB: number;
  padR: number;
  padT: number;
  termMonths: number;
  totalWidth: number;
  x: (i: number) => number;
  y: (value: number) => number;
}) {
  const point = history[index]!;
  const hx = x(index);
  const leftSide = hx + boxWidth + 12 > totalWidth - padR;
  const bx = leftSide ? hx - boxWidth - 12 : hx + 12;
  const anchorRate = point.baseRatePercent ?? point.personalizedRatePercent;
  const anchorY =
    typeof anchorRate === "number"
      ? y(anchorRate * 100) - boxHeight - 10
      : padT;
  const by = Math.min(
    Math.max(padT, anchorY),
    padT + innerHeight - boxHeight - padB,
  );
  const paymentAt =
    typeof anchorRate === "number"
      ? monthlyPayment(loanAmount, anchorRate * 100, termMonths)
      : null;

  return (
    <g style={{ pointerEvents: "none" }}>
      <line
        opacity="0.3"
        stroke="var(--primary)"
        x1={hx}
        x2={hx}
        y1={padT}
        y2={padT + innerHeight}
      />
      <rect
        fill="var(--card)"
        height={boxHeight}
        rx="10"
        stroke="var(--border-soft)"
        width={boxWidth}
        x={bx}
        y={by}
      />
      <text
        className="fill-muted-foreground"
        fontSize="9"
        fontWeight={700}
        letterSpacing="0.22em"
        x={bx + 12}
        y={by + 18}
      >
        {point.dateLabel.toUpperCase()}
      </text>
      <circle
        cx={bx + 14}
        cy={by + 36}
        fill="var(--card)"
        r="4"
        stroke="var(--muted-foreground)"
        strokeWidth="1.5"
      />
      <text
        className="fill-muted-foreground"
        fontSize="11"
        fontWeight={500}
        x={bx + 24}
        y={by + 40}
      >
        Base rate
      </text>
      <text
        className="font-bold tabular-nums fill-foreground"
        fontSize="12"
        fontWeight={650}
        textAnchor="end"
        x={bx + boxWidth - 12}
        y={by + 40}
      >
        {formatRatePercent(point.baseRatePercent)}
      </text>
      <text
        className="fill-muted-foreground"
        fontSize="11"
        fontWeight={500}
        x={bx + 12}
        y={by + 56}
      >
        Est. payment
      </text>
      <text
        className="font-bold tabular-nums fill-foreground"
        fontSize="12"
        fontWeight={650}
        textAnchor="end"
        x={bx + boxWidth - 12}
        y={by + 56}
      >
        {formatPayment(paymentAt)}
      </text>
    </g>
  );
}

function buildLinePath(
  history: ProductHistoryPoint[],
  pick: (point: ProductHistoryPoint) => number | null,
  x: (i: number) => number,
  y: (value: number) => number,
) {
  const segments: string[] = [];
  let started = false;
  history.forEach((point, i) => {
    const raw = pick(point);
    if (typeof raw !== "number") {
      started = false;
      return;
    }
    segments.push(`${started ? "L" : "M"}${x(i)},${y(raw * 100)}`);
    started = true;
  });
  return segments.length > 0 ? segments.join(" ") : null;
}

// ─────────────────────────────────────────────────────────────────────
// Product strip — 30-yr / 15-yr / 7/6 ARM / FHA with sparklines
// ─────────────────────────────────────────────────────────────────────

// ActiveProductFeePanel renders the per-product fee schedule details
// (FHA MIP, VA funding fee, USDA fees) or the LLPA points adjustment
// (Conventional, ARM 7/6) for the lane the user is currently viewing.
// Lives inside the "Your rate, decomposed" section so the fee context
// sits next to the rate it modifies, rather than competing for space
// on every tile in the column.
function ActiveProductFeePanel({
  product,
  loanAmount,
}: {
  product: DashboardRateWatchComparisonProduct;
  loanAmount: number;
}) {
  const fees = summarizeProductFees(product);
  const llpa = getLLPAPoints(product);

  if (fees.hasFees) {
    return (
      <div className="rounded-xl border border-border-soft p-4">
        <p className="eyebrow-label">Program fee on this loan</p>
        <p className="mt-1 text-sm font-semibold tabular-nums text-foreground">
          {fees.label} · {fees.compact}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{fees.sub}</p>
      </div>
    );
  }
  if (llpa !== null) {
    // Don't double-explain LLPA — the decomposition table above already
    // shows the adjustment landing on the rate. The Conventional/ARM
    // case is left intentionally empty so the panel collapses cleanly.
    return null;
  }
  // Conventional with neither LLPA nor program fees (rare — clean lane
  // for a top-band borrower) gets a small affirmation so the column
  // doesn't end on a confusing dead air after the program-fee note.
  return (
    <div className="rounded-xl border border-border-soft p-4">
      <p className="eyebrow-label">Program fees</p>
      <p className="mt-1 text-xs text-muted-foreground">
        No program fees on {formatCompactLoan(loanAmount)} for this lane.
      </p>
    </div>
  );
}

function ProductStrip({
  activeProduct,
  comparisonProducts,
  loanAmount,
  ratesShift,
  setActiveProduct,
  variant = "grid",
}: {
  activeProduct: DashboardRateWatchComparisonProduct;
  comparisonProducts: DashboardRateWatchComparisonProduct[];
  loanAmount: number;
  ratesShift: number;
  setActiveProduct: (id: string) => void;
  // "grid" is the default horizontal wrap (cols 2/3/7 by breakpoint);
  // "column" is a single vertical stack used when the strip is paired
  // side-by-side with the rate decomposition panel. Column variant drops
  // the per-tile spark chart and target-basis line to keep each row
  // compact — the basis is already stated in the page header.
  variant?: "grid" | "column";
}) {
  const isColumn = variant === "column";
  const listClassName = isColumn
    ? "grid grid-cols-2 gap-2"
    : "grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-7";
  return (
    <section className="space-y-3">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="eyebrow-label">Compare loan products</p>
        {!isColumn ? (
          <p className="text-xs text-muted-foreground">
            Selecting a lane retunes the chart and simulator.
          </p>
        ) : null}
      </header>
      <div className={listClassName}>
        {comparisonProducts.map((product) => {
          const badge = productBadge(product);
          const productHistory = buildProductHistory({
            history: product.history,
            productId: product.id,
            ratesShift,
          });
          const lastBase = productHistory.at(-1)?.baseRatePercent ?? null;
          const displayBase =
            lastBase ??
            (typeof product.baseRatePercent === "number"
              ? product.baseRatePercent + ratesShift / 10000
              : null);
          const displayPayment =
            ratesShift === 0 &&
            typeof product.estimatedPaymentMonthly === "number"
              ? product.estimatedPaymentMonthly
              : typeof displayBase === "number"
                ? monthlyPayment(
                    loanAmount,
                    displayBase * 100,
                    product.loanTermMonths,
                  )
                : null;
          const selected = product.id === activeProduct.id;
          return (
            <button
              key={product.id}
              aria-pressed={selected}
              className={cn(
                "group relative flex flex-col rounded-2xl border border-border bg-card text-left transition-colors",
                "hover:border-primary/40 hover:bg-primary/5",
                selected &&
                  "border-primary bg-primary/5 ring-2 ring-primary/25",
                isColumn ? "gap-1.5 p-3" : "gap-3 p-4",
              )}
              onClick={() => setActiveProduct(product.id)}
              type="button"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 text-sm font-semibold text-foreground">
                  {product.label}
                </span>
                <span
                  className={cn(
                    "shrink-0 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em]",
                    badge.className,
                  )}
                  title={badge.tooltip}
                >
                  {badge.label}
                </span>
              </div>
              <div>
                <p
                  className={cn(
                    "font-bold tabular-nums leading-tight",
                    isColumn ? "text-xl" : "text-[1.6rem]",
                    selected ? "text-primary" : "text-foreground",
                  )}
                >
                  {formatRatePercent(displayBase)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isColumn ? (
                    <>Base rate · {formatPayment(displayPayment)}</>
                  ) : (
                    <>
                      Base rate · {formatPayment(displayPayment)} ·{" "}
                      {formatProductCostSummary(product, loanAmount)}
                    </>
                  )}
                </p>
              </div>
              {!isColumn ? (
                <ProductSpark
                  history={productHistory}
                  tone={selected ? "primary" : "muted"}
                />
              ) : null}
              <span className="sr-only">
                Base rate at{" "}
                {formatRatePercent(lastBase ?? product.baseRatePercent ?? null)}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// productBadge resolves the visual badge for one comparison tile.
// SILL-326 two-layer model: the catalog supplies a base `badge` (e.g.
// "Derived" for editorial-only lanes) and the composer overlays
// `primaryOverlay = true` on the entry matching the user's target.
// Overlay wins over the base badge; we never branch on product strings.
function productBadge(product: DashboardRateWatchComparisonProduct): {
  className: string;
  label: string;
  tooltip?: string;
} {
  if (product.primaryOverlay) {
    return {
      className: "bg-primary/10 text-primary",
      label: "Primary",
    };
  }
  return {
    className: badgeToneClassName(product.badge.tone),
    label: product.badge.label,
    tooltip: product.badge.tooltip,
  };
}

function badgeToneClassName(tone: string): string {
  switch (tone) {
    case "warning":
      return "bg-warning/15 text-warning-foreground";
    case "accent":
      return "bg-accent text-accent-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function ProductSpark({
  history,
  tone,
}: {
  history: ProductHistoryPoint[];
  tone: "primary" | "muted";
}) {
  const values = history
    .map((point) => point.baseRatePercent)
    .filter((value): value is number => typeof value === "number");
  if (values.length < 2) {
    return <div className="h-6" aria-hidden="true" />;
  }
  const W = 120;
  const H = 24;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 0.0001);
  const x = (i: number) => (i / (values.length - 1)) * W;
  const y = (value: number) => H - ((value - min) / span) * (H - 4) - 2;
  const path = values
    .map((value, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(value)}`)
    .join(" ");
  return (
    <svg
      aria-hidden="true"
      className={cn(
        "h-6 w-full",
        tone === "primary" ? "text-primary" : "text-muted-foreground",
      )}
      viewBox={`0 0 ${W} ${H}`}
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Scenario simulator — rate shift + buy-points sliders
// ─────────────────────────────────────────────────────────────────────

function ScenarioSimulator({
  activeProduct,
  loanAmount,
  points,
  ratesShift,
  setPoints,
  setRatesShift,
}: {
  activeProduct: DashboardRateWatchComparisonProduct;
  loanAmount: number;
  points: number;
  ratesShift: number;
  setPoints: (value: number) => void;
  setRatesShift: (value: number) => void;
}) {
  const baseRateForProduct = activeProduct.baseRatePercent;
  const simulatedRate =
    typeof baseRateForProduct === "number"
      ? baseRateForProduct + ratesShift / 10000 - (points * 0.25) / 100
      : null;
  const simulatedPayment =
    typeof simulatedRate === "number"
      ? monthlyPayment(
          loanAmount,
          simulatedRate * 100,
          activeProduct.loanTermMonths,
        )
      : null;
  const baselinePayment =
    typeof activeProduct.estimatedPaymentMonthly === "number" && points === 0
      ? activeProduct.estimatedPaymentMonthly
      : typeof baseRateForProduct === "number"
        ? monthlyPayment(
            loanAmount,
            baseRateForProduct * 100,
            activeProduct.loanTermMonths,
          )
        : null;
  const paymentDiff =
    simulatedPayment !== null && baselinePayment !== null
      ? simulatedPayment - baselinePayment
      : null;
  const pointsCost = points * 0.01 * loanAmount;
  const monthsToBreakEven =
    points > 0 && paymentDiff !== null && Math.abs(paymentDiff) > 1
      ? Math.round(pointsCost / Math.abs(paymentDiff))
      : null;

  return (
    <section className="surface-panel bg-card p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-4">
        <div>
          <p className="eyebrow-label">Scenario simulator</p>
          <h2 className="mt-1 text-xl font-semibold tracking-[-0.02em] text-foreground">
            What if…
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Drag to model rate moves or a points buydown. Nothing locks — this
            is a sandbox.
          </p>
        </div>
        <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-right">
          <p className="eyebrow-label">Simulated rate</p>
          <p className="display-figure mt-1 text-[1.75rem] leading-tight text-primary">
            {formatRatePercent(simulatedRate)}
          </p>
          <p className="mt-1 text-sm font-semibold text-foreground">
            {formatPayment(simulatedPayment)}
            {paymentDiff !== null && Math.abs(paymentDiff) > 0.5 ? (
              <span
                className={cn(
                  "ml-2 rounded-md px-2 py-0.5 text-xs font-semibold",
                  paymentDiff < 0
                    ? "bg-primary/10 text-primary"
                    : "bg-destructive/10 text-destructive",
                )}
              >
                {formatSignedMoney(paymentDiff)}
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <label
              className="text-sm font-semibold text-foreground"
              htmlFor="rate-shift-slider"
            >
              Rates move
            </label>
            <span className="text-sm font-semibold tabular-nums text-primary">
              {formatSignedBps(ratesShift)}
            </span>
          </div>
          <Slider
            defaultValue={[0]}
            max={100}
            min={-100}
            onValueChange={(value) => setRatesShift(value[0] ?? 0)}
            step={25}
            value={[ratesShift]}
          />
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>−100 bps</span>
            <span>today</span>
            <span>+100 bps</span>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <label
              className="text-sm font-semibold text-foreground"
              htmlFor="points-slider"
            >
              Buy points
            </label>
            <span className="text-sm font-semibold tabular-nums text-primary">
              {points.toFixed(2)} pts · $
              {Math.round(pointsCost).toLocaleString()}
            </span>
          </div>
          <Slider
            defaultValue={[0]}
            max={3}
            min={0}
            onValueChange={(value) => setPoints(value[0] ?? 0)}
            step={0.25}
            value={[points]}
          />
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>0</span>
            <span>1.5 pts</span>
            <span>3 pts</span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
        {points > 0 && monthsToBreakEven !== null ? (
          <p className="text-sm leading-6 text-muted-foreground">
            <b className="font-semibold text-foreground">
              Break-even in ~{monthsToBreakEven} months
            </b>{" "}
            ({(monthsToBreakEven / 12).toFixed(1)} yrs). After that the buydown
            saves you money.
          </p>
        ) : (
          <p className="text-sm leading-6 text-muted-foreground">
            <Sparkles className="mr-1 inline h-3.5 w-3.5 text-primary" />
            Sandbox: we model the rate, not a lender's quote.
          </p>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// FAQ — pricing explainer accordion
// ─────────────────────────────────────────────────────────────────────

function PricingFAQ({
  activeProduct,
  onOpenFinancials,
}: {
  activeProduct: DashboardRateWatchComparisonProduct;
  onOpenFinancials: () => void;
}) {
  const activeLLPA = getLLPAPoints(activeProduct);
  const activeFees = summarizeProductFees(activeProduct);
  const improveCopy =
    activeLLPA !== null
      ? {
          intro: (
            <>
              Your {formatPoints(activeLLPA)} adjustment is driven by credit
              score and loan-to-value. Two levers:
            </>
          ),
          items: [
            "Raise your credit score into the next tier (740+) — typically drops the adjustment by ~0.375 pts.",
            "Hit 20% down — typically drops the adjustment by ~0.250 pts and removes PMI.",
          ],
        }
      : {
          intro: (
            <>
              {activeFees.label} is driven by the product program and
              eligibility inputs, not a conventional pricing-adjustment grid.
              Two levers:
            </>
          ),
          items: [
            "Tighten product eligibility inputs like location, occupancy, county, and program-specific status.",
            "Compare down-payment or exemption details when the program fee schedule supports a lower tier.",
          ],
        };
  const items: { q: string; a: ReactNode }[] = [
    {
      q: "Why is pricing shown in two parts?",
      a: (
        <>
          <p className="mb-2">
            Lender pricing has <b>two moving pieces</b> and they behave
            differently over time. Splitting them keeps it honest.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <FAQBlock chip="Base rate">
              The current source rate for this product lane.
            </FAQBlock>
            <FAQBlock chip="Pricing adjustment">
              Conventional fixed lanes are priced against the public Fannie
              Mae LLPA matrix and Freddie Mac Credit Fees in Price — a
              borrower&apos;s grid cell of credit, LTV, occupancy, and
              purpose. Adjustments are <b>quoted in points</b>, not rate.
            </FAQBlock>
            <FAQBlock chip="Product fees">
              FHA, VA, USDA, and PMI-style schedules show program fees that are
              included in the payment.
            </FAQBlock>
            <FAQBlock chip="Lender quote">
              A lender converts those points into rate, discount points, or
              credits using their daily rate sheet — execution and margin
              vary, so two lenders can quote the same grid cell differently.
            </FAQBlock>
          </div>
        </>
      ),
    },
    {
      q: "What unlocks a truer quote?",
      a: (
        <>
          <p>
            A real lender quote still decides the final pricing. Different
            executions turn the same adjustment into different note rates,
            discount points, or lender credits.
          </p>
          <ul className="mt-2 space-y-1.5">
            {[
              "Product-specific pricing and lock options",
              "Discount-point or lender-credit tradeoffs",
              "Current execution and margin strategy",
              "Live quote terms instead of a shortcut conversion",
            ].map((item) => (
              <li
                key={item}
                className="rounded-lg bg-muted/60 px-3 py-2 text-sm text-foreground"
              >
                {item}
              </li>
            ))}
          </ul>
        </>
      ),
    },
    {
      q: "How do I improve my pricing adjustment?",
      a: (
        <>
          <p>{improveCopy.intro}</p>
          <ul className="mt-2 space-y-1.5">
            {improveCopy.items.map((item) => (
              <li
                className="rounded-lg bg-muted/60 px-3 py-2 text-sm text-foreground"
                key={item}
              >
                {item}
              </li>
            ))}
          </ul>
          <button
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-dark"
            onClick={onOpenFinancials}
            type="button"
          >
            Compare the payment math in calculators
            <ArrowRight className="h-4 w-4" />
          </button>
        </>
      ),
    },
    {
      q: 'What does "modeled pricing" mean?',
      a: (
        <>
          <p>
            Sill models payment math from your profile, the current base rate
            for that product, and the pricing adjustments we can infer from
            the public agency grids.
          </p>
          <p className="mt-2">
            Today the model treats those adjustments as <b>additive</b> to
            the base rate (points added straight on). In a real quote a
            lender usually absorbs some of the points into discount or
            execution rather than the note rate, so your displayed rate
            tends to read on the conservative side. We&apos;re moving to a
            true point-to-rate conversion as lenders post live sheets.
          </p>
        </>
      ),
    },
  ];
  const [open, setOpen] = useState<number>(0);

  return (
    <section className="surface-panel bg-card p-5 sm:p-6">
      <p className="eyebrow-label">Pricing, explained</p>
      <h2 className="mt-1 text-xl font-semibold tracking-[-0.02em] text-foreground">
        How the math actually works.
      </h2>
      <div className="mt-4 divide-y divide-border border-y border-border">
        {items.map((item, index) => {
          const isOpen = index === open;
          return (
            <div key={item.q}>
              <button
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between py-3 text-left text-sm font-semibold text-foreground transition-colors hover:text-primary"
                onClick={() => setOpen(isOpen ? -1 : index)}
                type="button"
              >
                <span>{item.q}</span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform",
                    isOpen ? "rotate-180" : "rotate-0",
                  )}
                />
              </button>
              {isOpen ? (
                <div className="pb-4 text-sm leading-6 text-muted-foreground">
                  {item.a}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FAQBlock({ children, chip }: { children: ReactNode; chip: string }) {
  return (
    <div className="rounded-xl bg-muted/50 p-3">
      <span className="inline-block rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
        {chip}
      </span>
      <p className="mt-2 text-xs leading-5 text-foreground">{children}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// History construction — apply simulator shift
// ─────────────────────────────────────────────────────────────────────

function buildProductHistory({
  history,
  productId,
  ratesShift,
}: {
  history: DashboardRateWatchHistoryPoint[];
  productId: string;
  ratesShift: number;
}): ProductHistoryPoint[] {
  return history.map((point, index) => {
    const base = point.baseRatePercent;
    const your = point.personalizedRatePercent;
    const shift = ratesShift / 10000;
    return {
      key: `${productId}-${point.createdAt || index}`,
      dateLabel: point.dateLabel,
      baseRatePercent: typeof base === "number" ? base + shift : null,
      personalizedRatePercent: typeof your === "number" ? your + shift : null,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────
// Product pricing and fee summaries
// ─────────────────────────────────────────────────────────────────────

type ProductCostDisplay = {
  eyebrow: string;
  sub: string;
  value: string;
};

type ProductFeeSummary = {
  compact: string;
  financedUpfrontDollars: number;
  hasFees: boolean;
  label: string;
  monthlyDollars: number;
  sub: string;
  upfrontDollars: number;
  value: string;
};

function buildProductCostDisplay(
  product: DashboardRateWatchComparisonProduct,
  loanAmount: number,
): ProductCostDisplay {
  const llpa = getLLPAPoints(product);
  const fees = summarizeProductFees(product);

  if (llpa !== null) {
    return {
      eyebrow: "Pricing adj.",
      sub: `${formatPointsLoanTranslation(llpa, loanAmount)}${
        fees.hasFees ? `; ${fees.compact}` : ""
      }`,
      value: formatPoints(llpa),
    };
  }

  if (fees.hasFees) {
    return {
      eyebrow: "Product fees",
      sub: fees.sub,
      value: fees.value,
    };
  }

  return {
    eyebrow: "Pricing adj.",
    sub: "No modeled product fee for this lane.",
    value: "None",
  };
}

function buildWhatChangedPricingCopy(
  product: DashboardRateWatchComparisonProduct,
  loanAmount: number,
) {
  const llpa = getLLPAPoints(product);
  if (llpa !== null) {
    return `Your ${formatPoints(llpa)} pricing adjustment held — credit score and LTV haven't changed.`;
  }

  const fees = summarizeProductFees(product);
  if (fees.hasFees) {
    return `${fees.label} held in the product fee schedule; payment includes ${fees.compact}.`;
  }

  return `No product fee adjustment is modeled on ${formatCompactLoan(loanAmount)}.`;
}

function formatProductCostSummary(
  product: DashboardRateWatchComparisonProduct,
  loanAmount: number,
) {
  const llpa = getLLPAPoints(product);
  if (llpa !== null) {
    return `${formatPoints(llpa)} adj.`;
  }

  const fees = summarizeProductFees(product);
  if (fees.hasFees) {
    return fees.compact;
  }

  return `no fee on ${formatCompactLoan(loanAmount)}`;
}

function getLLPAPoints(product: DashboardRateWatchComparisonProduct) {
  const llpaLine = (product.pricingLines ?? []).find(
    (line) => line.kind === "llpa" && typeof line.points === "number",
  );
  if (typeof llpaLine?.points === "number") {
    return llpaLine.points;
  }
  if (
    (product.product === "conventional" || product.product === "arm_7_6") &&
    typeof product.llpaPoints === "number"
  ) {
    return product.llpaPoints;
  }
  return null;
}

function summarizeProductFees(
  product: DashboardRateWatchComparisonProduct,
): ProductFeeSummary {
  const lines = product.feeLines ?? [];
  const monthlyCents = lines
    .filter(isMonthlyFeeLine)
    .reduce((sum, line) => sum + line.amount, 0);
  const upfrontLines = lines.filter((line) => !isMonthlyFeeLine(line));
  const upfrontCents = upfrontLines.reduce((sum, line) => sum + line.amount, 0);
  const financedUpfrontCents = upfrontLines
    .filter((line) => line.financed)
    .reduce((sum, line) => sum + line.amount, 0);

  const monthlyDollars = centsToRoundedDollars(monthlyCents);
  const upfrontDollars = centsToRoundedDollars(upfrontCents);
  const financedUpfrontDollars = centsToRoundedDollars(financedUpfrontCents);
  const label = productFeeLabel(product);

  if (monthlyDollars > 0) {
    const compact =
      upfrontDollars > 0
        ? `${formatCurrencyDollars(monthlyDollars)}/mo + ${formatCurrencyDollars(
            upfrontDollars,
          )}${financedUpfrontDollars > 0 ? " financed" : " upfront"}`
        : `${formatCurrencyDollars(monthlyDollars)}/mo`;
    return {
      compact,
      financedUpfrontDollars,
      hasFees: true,
      label,
      monthlyDollars,
      sub:
        upfrontDollars > 0
          ? `${label}: ${formatCurrencyDollars(
              upfrontDollars,
            )}${financedUpfrontDollars > 0 ? " financed" : " upfront"} plus monthly charge.`
          : `${label} modeled as a monthly fee.`,
      upfrontDollars,
      value: `${formatCurrencyDollars(monthlyDollars)}/mo`,
    };
  }

  if (upfrontDollars > 0) {
    return {
      compact: `${formatCurrencyDollars(upfrontDollars)}${
        financedUpfrontDollars > 0 ? " financed" : " upfront"
      }`,
      financedUpfrontDollars,
      hasFees: true,
      label,
      monthlyDollars,
      sub: `${label}${financedUpfrontDollars > 0 ? " is financed into the loan amount." : " is modeled upfront."}`,
      upfrontDollars,
      value: formatCurrencyDollars(upfrontDollars),
    };
  }

  return {
    compact: "no modeled fee",
    financedUpfrontDollars: 0,
    hasFees: false,
    label,
    monthlyDollars: 0,
    sub: "No modeled product fee for this lane.",
    upfrontDollars: 0,
    value: "None",
  };
}

function isMonthlyFeeLine(line: DashboardRateWatchFeeLine) {
  return [
    "pmi",
    "fha_annual_mip",
    "monthly_product_fee",
    "usda_annual_fee",
  ].includes(line.kind);
}

function productFeeLabel(product: DashboardRateWatchComparisonProduct) {
  const lines = product.feeLines ?? [];
  if (lines.some((line) => line.kind === "pmi")) {
    return "PMI";
  }
  if (lines.some((line) => line.kind.startsWith("fha_"))) {
    return "FHA MIP";
  }
  if (lines.some((line) => line.kind === "va_funding_fee")) {
    return "VA funding fee";
  }
  if (lines.some((line) => line.kind.startsWith("usda_"))) {
    return "USDA fees";
  }
  return "Product fees";
}

// ─────────────────────────────────────────────────────────────────────
// Math + formatting helpers
// ─────────────────────────────────────────────────────────────────────

function monthlyPayment(
  principal: number,
  ratePercent: number,
  termMonths: number,
) {
  const r = ratePercent / 100 / 12;
  if (r === 0) return principal / termMonths;
  return (principal * r) / (1 - Math.pow(1 + r, -termMonths));
}

function formatRatePercent(value: number | null) {
  if (typeof value !== "number") return "—";
  return `${formatScaledPercentage(value)}%`;
}

function formatSignedPercent(value: number | null) {
  if (typeof value !== "number") return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${formatScaledPercentage(value)}%`;
}

function formatPercentMagnitude(value: number) {
  return `${formatScaledPercentage(Math.abs(value))}%`;
}

function formatScaledPercentage(value: number) {
  return trimTrailingZeros((value * 100).toFixed(3));
}

function formatPoints(value: number | null) {
  if (typeof value !== "number") return "—";
  const scaled = trimTrailingZeros((value * 100).toFixed(3));
  return `${scaled} pts`;
}

function formatPointsLoanTranslation(llpa: number | null, loanAmount: number) {
  if (typeof llpa !== "number") return "(pricing pending)";
  const cost = Math.round(llpa * loanAmount);
  return `≈ $${cost.toLocaleString()} on ${formatCompactLoan(loanAmount)}`;
}

function formatPayment(value: number | null) {
  if (typeof value !== "number") return "—";
  return `$${Math.round(value).toLocaleString()}/mo`;
}

function formatCurrencyDollars(value: number) {
  return `$${Math.round(value).toLocaleString()}`;
}

function centsToRoundedDollars(value: number) {
  return Math.round(value / 100);
}

function formatSignedMoney(value: number | null, suffix: string = "") {
  if (typeof value !== "number") return "";
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  const amount = Math.abs(Math.round(value));
  return `${sign}$${amount.toLocaleString()}${suffix ? `/${suffix}` : ""}`;
}

function formatSignedBps(ratesShift: number) {
  if (!Number.isFinite(ratesShift) || ratesShift === 0) return "0 bps";
  const sign = ratesShift > 0 ? "+" : "−";
  return `${sign}${Math.abs(ratesShift)} bps`;
}

function formatPricingSourceLabel(sourceName: string | null) {
  switch (sourceName) {
    case "mnd":
      return "MND";
    case "fred_pmms":
      return "FRED PMMS";
    case null:
    case "":
      return "Current pricing source";
    default:
      return sourceName
        .replaceAll("_", " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
}

function formatCompactLoan(value: number) {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${value.toLocaleString()}`;
}

function formatTargetBasis(value: number, location: string | null) {
  const price = formatCompactLoan(value);
  const cleanLocation = location?.trim();
  return cleanLocation ? `${price} in ${cleanLocation}` : `${price} target`;
}
